import type { ReactElement } from 'react';
import {
  HomeOutlined,
  LineChartOutlined,
  ShoppingOutlined,
  BookOutlined,
  DashboardOutlined,
  UserOutlined,
  SafetyCertificateOutlined,
  SyncOutlined,
  AuditOutlined,
  CloudServerOutlined,
  ExperimentOutlined,
  NodeIndexOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  BgColorsOutlined,
  BugOutlined,
  MailOutlined,
  ReadOutlined,
} from '@ant-design/icons';
import { RBAC_PERMISSIONS } from './rbac';

const NAV_ICON_STYLE = { fontSize: 16 } as const;

export interface NavItem {
  key: string;
  label: string;
  path: string;
  icon: ReactElement;
  /** Строковое имя иконки для config-driven переопределения */
  defaultIconName: string;
  rbac?: { resource: string; permission: string };
  featureFlag?: string;
}

export interface AdminNavGroup {
  key: string;
  label: string;
  children: AdminNavItem[];
}

export interface AdminNavItem {
  key: string;
  label: string;
  path: string;
  icon: ReactElement;
}


export const MAIN_NAVIGATION: NavItem[] = [
  {
    key: 'home',
    label: 'Главная',
    path: '/',
    icon: <HomeOutlined style={NAV_ICON_STYLE} />,
    defaultIconName: 'HomeOutlined',
  },
  {
    key: 'flow',
    label: 'Planning Flow',
    path: '/flow',
    icon: <NodeIndexOutlined style={NAV_ICON_STYLE} />,
    defaultIconName: 'NodeIndexOutlined',
    featureFlag: 'planning_flow',
  },
  {
    key: 'sales',
    label: 'План продаж',
    path: '/sales',
    icon: <LineChartOutlined style={NAV_ICON_STYLE} />,
    defaultIconName: 'LineChartOutlined',
    rbac: { resource: 'sales', permission: RBAC_PERMISSIONS.READ },
  },
  {
    key: 'purchase',
    label: 'План закупок',
    path: '/purchase',
    icon: <ShoppingOutlined style={NAV_ICON_STYLE} />,
    defaultIconName: 'ShoppingOutlined',
    rbac: { resource: 'purchase', permission: RBAC_PERMISSIONS.READ },
  },
  {
    key: 'reconciliation',
    label: 'Сверка',
    path: '/reconciliation',
    icon: <CheckCircleOutlined style={NAV_ICON_STYLE} />,
    defaultIconName: 'CheckCircleOutlined',
    rbac: { resource: 'reconciliation', permission: RBAC_PERMISSIONS.READ },
  },
  {
    key: 'references',
    label: 'Управление',
    path: '/references',
    icon: <BookOutlined style={NAV_ICON_STYLE} />,
    defaultIconName: 'BookOutlined',
    rbac: { resource: 'references', permission: RBAC_PERMISSIONS.READ },
  },
  {
    key: 'methodology',
    label: 'Методология',
    path: '/methodology',
    icon: <ReadOutlined style={NAV_ICON_STYLE} />,
    defaultIconName: 'ReadOutlined',
    rbac: { resource: 'methodology', permission: RBAC_PERMISSIONS.READ },
  },
];


export const ADMIN_TOP_ITEMS: AdminNavItem[] = [
  {
    key: 'dashboard',
    icon: <DashboardOutlined />,
    label: 'Обзор',
    path: '/admin',
  },
  {
    key: 'monitoring',
    icon: <CloudServerOutlined />,
    label: 'Мониторинг',
    path: '/admin/monitoring',
  },
  {
    key: 'errors',
    icon: <BugOutlined />,
    label: 'Ошибки',
    path: '/admin/errors',
  },
];

export const ADMIN_GROUPS: AdminNavGroup[] = [
  {
    key: 'access',
    label: 'Управление доступом',
    children: [
      {
        key: 'users',
        icon: <UserOutlined />,
        label: 'Пользователи',
        path: '/admin/users',
      },
      {
        key: 'roles',
        icon: <SafetyCertificateOutlined />,
        label: 'Роли и Права',
        path: '/admin/roles',
      },
      {
        key: 'sync',
        icon: <SyncOutlined />,
        label: 'Синхронизация',
        path: '/admin/sync',
      },
    ],
  },
  {
    key: 'system',
    label: 'Система',
    children: [
      {
        key: 'audit',
        icon: <AuditOutlined />,
        label: 'Журнал аудита',
        path: '/admin/audit',
      },
      {
        key: 'email-notifications',
        icon: <MailOutlined />,
        label: 'Почтовые уведомления',
        path: '/admin/email-notifications',
      },
      {
        key: 'feature-flags',
        icon: <ExperimentOutlined />,
        label: 'Feature Flags',
        path: '/admin/feature-flags',
      },
      {
        key: 'methodology',
        icon: <ReadOutlined />,
        label: 'Методология',
        path: '/admin/methodology',
      },
      {
        key: 'page-comments',
        icon: <MessageOutlined />,
        label: 'Комментарии страниц',
        path: '/admin/page-comments',
      },
      {
        key: 'ui-config',
        icon: <BgColorsOutlined />,
        label: 'Настройки UI',
        path: '/admin/ui-config',
      },
    ],
  },
];


/** Все admin-элементы (top-level + из групп) в плоском списке */
export const ALL_ADMIN_ITEMS: AdminNavItem[] = [
  ...ADMIN_TOP_ITEMS,
  ...ADMIN_GROUPS.flatMap((g) => g.children),
];

/** Определяет активный ключ навигации по текущему пути */
export function getActiveNavKey(pathname: string): string {
  for (const item of MAIN_NAVIGATION) {
    if (item.key === 'home') continue;
    if (pathname.startsWith(item.path)) return item.key;
  }
  return 'home';
}

/** Определяет активный ключ в админ-меню по текущему пути */
export function getActiveAdminKey(pathname: string): string[] {
  // Точное совпадение для dashboard
  if (pathname === '/admin' || pathname === '/admin/') return ['dashboard'];

  for (const item of ALL_ADMIN_ITEMS) {
    if (item.key === 'dashboard') continue;
    if (pathname.startsWith(item.path)) return [item.key];
  }

  return ['dashboard'];
}
